import {useRef, useCallback, useState, useEffect} from 'react';
import {FC} from 'react';
import {styled} from 'styled-components';

import {Input} from '@shared_web/components/core/input';

export interface InputCodeVerificationProps {
  validationCode: string[];
  setValidationCode: (code: string[]) => void;
  onEnter: () => void;
}

export const InputCodeVerification: FC<InputCodeVerificationProps> = ({
  validationCode,
  setValidationCode,
  onEnter,
}) => {
  const inputIds = ['digit-0', 'digit-1', 'digit-2', 'digit-3', 'digit-4', 'digit-5'];

  const inputRefs = useRef<(HTMLInputElement | undefined)[]>([]);

  // State to control whether to call onEnter when the last digit is entered
  // This is to prevent calling onEnter when the user is still typing
  // and only call it when the last digit is entered
  const [shouldCallOnEnter, setShouldCallOnEnter] = useState<boolean>(false);

  useEffect(() => {
    if (shouldCallOnEnter) {
      setShouldCallOnEnter(false); // Reset the flag after calling onEnter
      if (validationCode.join('').length === 6) {
        onEnter();
      }
    }
  }, [shouldCallOnEnter, onEnter, validationCode]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const index = e.currentTarget.getAttribute('data-index');
      if (index === null) {
        console.error('Index not found in input element');
        return;
      }
      const indexNumber = parseFloat(index);
      if (indexNumber < 0 || indexNumber >= validationCode.length) return;
      const {value} = e.currentTarget;
      if (!/^\d?$/.test(value)) {
        return;
      } // Prevent more than one character input
      const newCode = [...validationCode];
      newCode[indexNumber] = value;
      setValidationCode(newCode);

      if (value && indexNumber < validationCode.length - 1) {
        const nextInput = inputRefs.current[indexNumber + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }

      if (value && indexNumber === validationCode.length - 1) {
        setShouldCallOnEnter(true);
      }
    },
    [validationCode, setValidationCode]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;
      const index = e.currentTarget.getAttribute('data-index');
      if (index === null) {
        console.error('Index not found in input element');
        return;
      }
      const indexNumber = parseFloat(index);
      // const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter'];
      // const isDigit = /^\d$/.test(key);
      // // biome-ignore lint/complexity/useSimplifiedLogicExpression: false positive
      // if (!isDigit && !allowedKeys.includes(key)) {
      //   e.preventDefault();
      //   return; // Ignore les touches non autorisées
      // }
      if (key === 'Backspace') {
        const newCode = [...validationCode];
        if (validationCode[indexNumber] !== '') {
          newCode[indexNumber] = '';
          setValidationCode(newCode);
        } else if (indexNumber > 0) {
          const prevInput = inputRefs.current[indexNumber - 1];
          if (prevInput) {
            prevInput.focus();
          }
        }
      }
      if (key === 'Enter') {
        e.preventDefault();
        onEnter();
        return;
      }
    },
    [validationCode, setValidationCode, onEnter]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      if (!/^\d{6}$/.test(pastedData)) {
        return; // Ignore if pasted data is not exactly 6 digits
      }
      const newCode = pastedData.split('');
      setValidationCode(newCode);
      setShouldCallOnEnter(true); // Call onEnter after pasting
    },
    [setValidationCode]
  );

  const setInputRefs = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      if (el) {
        inputRefs.current[index] = el;
      } else {
        inputRefs.current[index] = undefined; // Clear the ref if the element is null
      }
    },
    []
  );

  return (
    <Wrapper>
      {validationCode.map((code, index) => (
        <InputStyled
          key={inputIds[index]}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          value={code}
          maxLength={1}
          data-index={index}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          ref={setInputRefs(index)}
          onPaste={handlePaste}
        />
      ))}
    </Wrapper>
  );
};

InputCodeVerification.displayName = 'InputCodeVerification';

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const InputStyled = styled(Input)`
  width: 50px;
  height: 50px;
  text-align: center;

  &:hover,
  &:focus {
    border-color: black;
  }

  @media (max-width: 375px) {
    width: 40px;
    height: 40px;
  }
`;
